import { Navigate, useOutletContext } from 'react-router-dom'

import {
  CompetenciesProfileRootPageDocument,
  CompetenciesProfileRootPageQuery,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { usePagination } from 'modules/competencies/hooks/usePagination'
import CollectionsBootstrap from 'modules/competencies/lib/ProfileCollectionsBootstrap'
import Competencies from 'modules/competencies/pages/employees/Competencies'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RouteContext } from 'modules/profile/pages/RootPage'

interface ContainerProps {
  data?: CompetenciesProfileRootPageQuery
  onNextPage: () => void
  onPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  isEmpty: boolean
}

const Container = ({
  data,
  onNextPage,
  onPreviousPage,
  hasNextPage,
  hasPreviousPage,
  isEmpty,
}: ContainerProps) => {
  const { navigation } = useNavigation()
  const { competenciesAvailable } = useOutletContext<RouteContext>()

  const canManageCompetencies = data?.competencies._policies.competenciesManage.can ?? false

  if (!competenciesAvailable && canManageCompetencies)
    return <Navigate replace to={navigation.discover.marketplaceModules.fullPath()} />

  if (!competenciesAvailable) return <NotFound />

  return (
    <Competencies
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      assessmentGroup={data?.competencies.assessmentGroupsConnection.edges[0]?.node}
      isEmpty={isEmpty}
    />
  )
}

const RootPage = () => {
  const { employee } = useOutletContext<RouteContext>()

  const { pageNumber, handleNextPage, handlePreviousPage } = usePagination()

  const { loading, data, error } = useQuery(CompetenciesProfileRootPageDocument, {
    variables: {
      targetEmployeeIds: [employee.id.toString()],
      offsetPage: pageNumber,
    },
  })

  const pageInfo = data?.competencies.assessmentGroupsConnection.pageInfo
  const totalCount = data?.competencies.assessmentGroupsConnection.totalCount ?? 0
  const isEmpty = totalCount === 0

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="full">
      <CollectionsBootstrap>
        <Container
          data={data}
          onNextPage={() => handleNextPage(totalCount)}
          onPreviousPage={handlePreviousPage}
          hasNextPage={pageInfo?.hasNextPage ?? false}
          hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
          isEmpty={isEmpty}
        />
      </CollectionsBootstrap>
    </ResponsivePage.Body>
  )
}

export default RootPage
